// Cognito's API, as TUNE Analytics calls it from the browser to sign people in: one POST for each operation, named by
// X-Amz-Target, to Cognito in AWS or to its stand-in on this machine (server/src/local/cognito-api.ts). No password is
// kept: it goes to Cognito over TLS, once, as the person types it.

export class CognitoError extends Error {
  constructor(
    /** Cognito's name for what went wrong: NotAuthorizedException, CodeMismatchException… */
    readonly type: string,
    message: string,
  ) {
    super(message || type);
  }
}

export interface AuthResult {
  AccessToken: string;
  IdToken: string;
  /** Not there when a refresh keeps the same one. */
  RefreshToken?: string;
  ExpiresIn: number;
}

/** What signing in answers: tokens, or a step still to take. */
export type Initiated =
  | { AuthenticationResult: AuthResult; ChallengeName?: undefined }
  | {
      ChallengeName: string;
      Session: string;
      ChallengeParameters?: Record<string, string>;
      AuthenticationResult?: undefined;
    };

export interface Delivery {
  CodeDeliveryDetails?: { Destination?: string; DeliveryMedium?: string };
}

export type Fetch = (input: string, init: RequestInit) => Promise<Response>;

/** Cognito's error name, from its body or header: "NotAuthorizedException", without a namespace. */
function errorType(body: Record<string, unknown>, header: string | null): string {
  const raw = typeof body.__type === 'string' ? body.__type : (header ?? 'UnknownError');
  return raw.split('#').pop()?.split(':')[0] ?? 'UnknownError';
}

export interface Cognito {
  initiateAuth(clientId: string, email: string, password: string): Promise<Initiated>;
  respond(clientId: string, challenge: string, session: string, responses: Record<string, string>): Promise<Initiated>;
  associateToken(session: string): Promise<{ SecretCode: string; Session?: string }>;
  verifyToken(session: string, code: string): Promise<{ Status: string; Session?: string }>;
  signUp(
    clientId: string,
    email: string,
    password: string,
    attributes: Record<string, string>,
    metadata?: Record<string, string>,
  ): Promise<{ UserConfirmed: boolean } & Delivery>;
  confirmSignUp(clientId: string, email: string, code: string): Promise<unknown>;
  resendCode(clientId: string, email: string): Promise<Delivery>;
  forgotPassword(clientId: string, email: string): Promise<Delivery>;
  confirmForgotPassword(clientId: string, email: string, code: string, password: string): Promise<unknown>;
  changePassword(accessToken: string, previous: string, proposed: string): Promise<unknown>;
  refresh(clientId: string, refreshToken: string): Promise<{ AuthenticationResult: AuthResult }>;
  revoke(clientId: string, refreshToken: string): Promise<unknown>;
  globalSignOut(accessToken: string): Promise<unknown>;
}

/** A client for Cognito's API at `endpoint`. */
export function cognito(endpoint: string, fetcher: Fetch = (input, init) => fetch(input, init)): Cognito {
  async function call<T>(operation: string, body: object): Promise<T> {
    let res: Response;
    try {
      res = await fetcher(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
        },
        body: JSON.stringify(body),
        cache: 'no-store',
        credentials: 'omit',
      });
    } catch {
      throw new CognitoError('NetworkError', "Cognito didn't answer");
    }
    let parsed: Record<string, unknown> = {};
    try {
      const text = await res.text();
      parsed = text ? (JSON.parse(text) as Record<string, unknown>) : {};
    } catch {
      // A body that isn't JSON says nothing more than the status.
    }
    if (!res.ok) {
      throw new CognitoError(
        errorType(parsed, res.headers.get('x-amzn-ErrorType')),
        typeof parsed.message === 'string' ? parsed.message : '',
      );
    }
    return parsed as T;
  }

  return {
    initiateAuth: (clientId, email, password) =>
      call('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: { USERNAME: email, PASSWORD: password },
      }),
    respond: (clientId, challenge, session, responses) =>
      call('RespondToAuthChallenge', {
        ClientId: clientId,
        ChallengeName: challenge,
        Session: session,
        ChallengeResponses: responses,
      }),
    associateToken: (session) => call('AssociateSoftwareToken', { Session: session }),
    verifyToken: (session, code) =>
      call('VerifySoftwareToken', { Session: session, UserCode: code, FriendlyDeviceName: 'TUNE staff' }),
    signUp: (clientId, email, password, attributes, metadata) =>
      call('SignUp', {
        ClientId: clientId,
        Username: email,
        Password: password,
        UserAttributes: Object.entries(attributes).map(([Name, Value]) => ({ Name, Value })),
        ...(metadata ? { ClientMetadata: metadata } : {}),
      }),
    confirmSignUp: (clientId, email, code) =>
      call('ConfirmSignUp', { ClientId: clientId, Username: email, ConfirmationCode: code }),
    resendCode: (clientId, email) => call('ResendConfirmationCode', { ClientId: clientId, Username: email }),
    forgotPassword: (clientId, email) => call('ForgotPassword', { ClientId: clientId, Username: email }),
    confirmForgotPassword: (clientId, email, code, password) =>
      call('ConfirmForgotPassword', {
        ClientId: clientId,
        Username: email,
        ConfirmationCode: code,
        Password: password,
      }),
    changePassword: (accessToken, previous, proposed) =>
      call('ChangePassword', { AccessToken: accessToken, PreviousPassword: previous, ProposedPassword: proposed }),
    refresh: (clientId, refreshToken) =>
      call('GetTokensFromRefreshToken', { ClientId: clientId, RefreshToken: refreshToken }),
    revoke: (clientId, refreshToken) => call('RevokeToken', { ClientId: clientId, Token: refreshToken }),
    globalSignOut: (accessToken) => call('GlobalSignOut', { AccessToken: accessToken }),
  };
}
